import { Autocomplete, Box, CircularProgress, TextField, Typography, useTheme, SxProps, Theme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface FilterOption {
    id: string;
    label: string;
    symbol?: string;
    group?: string;
}

interface AutocompleteFilterProps {
    options: FilterOption[];
    value: string[];
    onChange: (newValue: string[]) => void;
    label: string;
    loading?: boolean;
    placeholder?: string;
    multiple?: boolean;
    sx?: SxProps<Theme>;
}

const AutocompleteFilter: React.FC<AutocompleteFilterProps> = ({
    options,
    value,
    onChange,
    label,
    loading = false,
    placeholder,
    multiple = true,
    sx,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    
    const defaultPlaceholder = t('transactions.filters.autocomplete.defaultPlaceholder');
    const selectedOptions = options.filter((option) => value.includes(option.id));
    const getLabelText = () => {
        if (selectedOptions.length === 0) return label;
        return `${label} (${selectedOptions.length})`;
    };

    return (
        <Box sx={{ minWidth: '150px', flex: 1, ...sx }}>
            <Autocomplete
                multiple={multiple}
                options={options}
                value={selectedOptions}
                onChange={(_, newValue) => {
                    if (!newValue) {
                        onChange([]);
                    } else if (Array.isArray(newValue)) {
                        onChange(newValue.map((v) => v.id));
                    } else {
                        onChange([newValue.id]);
                    }
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableCloseOnSelect={true}
                size="small"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={getLabelText()}
                        placeholder={selectedOptions.length === 0 ? placeholder || defaultPlaceholder : ''}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                            },
                            '& .MuiAutocomplete-endAdornment': {
                                right: '8px',
                            }
                        }}
                    />
                )}
                renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                        <Box component="li" key={option.id} {...otherProps}>
                            <Typography component="span">{option.label}</Typography>
                        </Box>
                    );
                }}
                renderTags={() => null}

                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                        },
                    },
                }}
            />
        </Box>
    );
};

export default AutocompleteFilter;
